import { useState, useEffect } from 'react';
import { supabase } from '../../supabase/client';
import QuotaAdjustmentModal from './QuotaAdjustmentModal';
import QuotaAuditLog from './QuotaAuditLog';
import QuotaSearchFilters from './QuotaSearchFilters';

interface QuotaFilters {
  planType: string;
  quotaStatus: string;
  dateRange: string;
  minQuota: string;
  maxQuota: string;
}
import BulkQuotaActions from './BulkQuotaActions';

interface User {
  id: string;
  email: string;
  plan_type: string;
  quota: number;
  updated_at: string;
}

interface QuotaOverviewData extends User {
  quota_status: 'critical' | 'warning' | 'normal';
  last_adjustment_reason?: string;
  last_adjustment_date?: string;
  last_adjusted_by?: string;
}

export default function AdminQuotaManagement() {
  const [users, setUsers] = useState<QuotaOverviewData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<QuotaFilters>({
    planType: '',
    quotaStatus: '',
    dateRange: '',
    minQuota: '',
    maxQuota: ''
  });
  const [selectedUser, setSelectedUser] = useState<QuotaOverviewData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchQuotaOverview();
  }, []);

  const fetchQuotaOverview = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to fetch from the admin_quota_overview view
      const { data, error: fetchError } = await supabase
        .from('admin_quota_overview')
        .select('*')
        .order('email');

      if (fetchError) {
        // Fallback to basic users table if view doesn't exist
        console.log('Quota overview view not available, using fallback:', fetchError);
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('users')
          .select('id, email, role, export_quota_remaining, created_at')
          .in('role', ['user', 'csr', 'manager'])
          .order('email');

        if (fallbackError) throw fallbackError;

        // Transform to match expected format
        const transformedData = fallbackData?.map(user => ({
          id: user.id,
          email: user.email,
          plan_type: user.role, // Use role as plan_type fallback
          quota: user.export_quota_remaining || 0,
          updated_at: user.created_at, // Use created_at as fallback
          quota_status: (user.export_quota_remaining || 0) < 10 ? 'critical' as const : 
                       (user.export_quota_remaining || 0) < 50 ? 'warning' as const : 'normal' as const
        })) || [];

        setUsers(transformedData);
      } else {
        setUsers(data || []);
      }
    } catch (err) {
      console.error('Error fetching quota overview:', err);
      setError('Unable to load quota data. Database schema may need to be deployed.');
      
      // Show preview data for development
      setUsers([
        {
          id: '1',
          email: 'user@example.com',
          plan_type: 'junior_sdr',
          quota: 45,
          updated_at: new Date().toISOString(),
          quota_status: 'warning',
          last_adjustment_reason: 'Purchased 100 lead credits',
          last_adjustment_date: new Date().toISOString(),
          last_adjusted_by: 'stripe_webhook'
        },
        {
          id: '2',
          email: 'john@company.com',
          plan_type: 'top_qualifier_sdr',
          quota: 250,
          updated_at: new Date().toISOString(),
          quota_status: 'normal'
        },
        {
          id: '3',
          email: 'mary@startup.com',
          plan_type: 'junior_sdr',
          quota: 2,
          updated_at: new Date().toISOString(),
          quota_status: 'critical',
          last_adjustment_reason: 'Lead export - 48 credits used',
          last_adjustment_date: new Date().toISOString(),
          last_adjusted_by: 'system'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    // Basic search filter
    const matchesSearch = !searchTerm || 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.plan_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.id.toLowerCase().includes(searchTerm.toLowerCase());

    // Plan type filter
    const matchesPlanType = !filters.planType || user.plan_type === filters.planType;

    // Quota status filter
    const matchesQuotaStatus = !filters.quotaStatus || user.quota_status === filters.quotaStatus;

    // Quota range filter
    const minQuota = filters.minQuota ? parseInt(filters.minQuota) : 0;
    const maxQuota = filters.maxQuota ? parseInt(filters.maxQuota) : Infinity;
    const matchesQuotaRange = user.quota >= minQuota && user.quota <= maxQuota;

    // Date range filter
    let matchesDateRange = true;
    if (filters.dateRange) {
      const userDate = new Date(user.updated_at);
      const now = new Date();
      const diffMs = now.getTime() - userDate.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);

      switch (filters.dateRange) {
        case 'today':
          matchesDateRange = diffDays < 1;
          break;
        case 'week':
          matchesDateRange = diffDays < 7;
          break;
        case 'month':
          matchesDateRange = diffDays < 30;
          break;
        case 'quarter':
          matchesDateRange = diffDays < 90;
          break;
      }
    }

    return matchesSearch && matchesPlanType && matchesQuotaStatus && matchesQuotaRange && matchesDateRange;
  });

  const getQuotaStatusBadge = (status: string) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'critical':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'warning':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-green-100 text-green-800`;
    }
  };

  const getPlanTypeBadge = (planType: string) => {
    const baseClasses = "inline-flex px-2 py-1 text-xs font-semibold rounded-full";
    
    switch (planType) {
      case 'top_qualifier_sdr':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'junior_sdr':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const exportToCSV = () => {
    const headers = ['Email', 'Plan Type', 'Quota', 'Status', 'Last Updated', 'Last Adjustment', 'Adjusted By'];
    const csvContent = [
      headers.join(','),
      ...filteredUsers.map(user => [
        user.email,
        formatPlanType(user.plan_type),
        user.quota.toString(),
        user.quota_status,
        formatDate(user.updated_at),
        user.last_adjustment_reason || 'None',
        user.last_adjusted_by || 'N/A'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quota-management-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleUserSelection = (userId: string, isSelected: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (isSelected) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map(user => user.id)));
    }
  };

  const clearSelection = () => {
    setSelectedUsers(new Set());
  };

  const getSelectedUsersData = () => {
    return filteredUsers.filter(user => selectedUsers.has(user.id));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPlanType = (planType: string) => {
    switch (planType) {
      case 'junior_sdr':
        return 'Junior SDR';
      case 'top_qualifier_sdr':
        return 'Top Qualifier';
      default:
        return planType;
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Quota Management</h2>
        <p className="text-gray-600 mt-1">
          Manage user quotas, plan types, and view usage analytics
        </p>
      </div>

      {/* Search and Filters */}
      <QuotaSearchFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onFiltersChange={setFilters}
        onExportCSV={exportToCSV}
        totalResults={filteredUsers.length}
        loading={loading}
      />

      {/* Actions Bar */}
      <div className="mb-6 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {filteredUsers.length !== users.length ? (
            <>Showing {filteredUsers.length} of {users.length} users</>
          ) : (
            <>Showing {users.length} user{users.length !== 1 ? 's' : ''}</>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={fetchQuotaOverview}
            disabled={loading}
            className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            onClick={clearSelection}
            disabled={selectedUsers.size === 0}
            className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear Selection ({selectedUsers.size})
          </button>
        </div>
      </div>

      {/* Bulk Actions */}
      <BulkQuotaActions
        selectedUsers={getSelectedUsersData()}
        onClearSelection={clearSelection}
        onSuccess={fetchQuotaOverview}
      />

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <p className="text-sm text-yellow-800">
                <strong>Database Schema Required:</strong> {error}
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                Showing preview data. Run the quota management SQL schema to enable full functionality.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quota Overview Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedUsers.size > 0 && selectedUsers.size === filteredUsers.length}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2">User</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quota Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Adjustment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                      <span className="text-gray-500">Loading quota data...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      {searchTerm ? 'No users found matching your search.' : 'No quota data available.'}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.id)}
                          onChange={(e) => handleUserSelection(user.id, e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="flex-shrink-0 h-10 w-10 ml-3">
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">
                              {user.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.email}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {user.id.slice(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getPlanTypeBadge(user.plan_type)}>
                        {formatPlanType(user.plan_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={getQuotaStatusBadge(user.quota_status)}>
                          {user.quota} credits
                        </span>
                        {user.quota_status === 'critical' && (
                          <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.updated_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.last_adjustment_reason ? (
                        <div>
                          <div className="text-sm text-gray-900">{user.last_adjustment_reason}</div>
                          <div className="text-xs text-gray-500">
                            by {user.last_adjusted_by || 'System'} • {user.last_adjustment_date ? formatDate(user.last_adjustment_date) : 'N/A'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">No adjustments</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setIsModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Adjust Quota
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Total Users</div>
          <div className="text-2xl font-bold text-gray-900">{users.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Critical Status</div>
          <div className="text-2xl font-bold text-red-600">
            {users.filter(u => u.quota_status === 'critical').length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Warning Status</div>
          <div className="text-2xl font-bold text-yellow-600">
            {users.filter(u => u.quota_status === 'warning').length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Total Credits</div>
          <div className="text-2xl font-bold text-green-600">
            {users.reduce((sum, u) => sum + u.quota, 0)}
          </div>
        </div>
      </div>

      {/* Quota Audit Log */}
      <div className="mt-8">
        <QuotaAuditLog limit={20} />
      </div>

      {/* Quota Adjustment Modal */}
      <QuotaAdjustmentModal
        user={selectedUser}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={() => {
          fetchQuotaOverview();
        }}
      />
    </div>
  );
}